import { Group, Grid, TextInput, Button, Accordion, JsonInput, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { addLocation, updateLocation, deleteLocation, ILocation } from '../../../libraries/location';
import CustomModal from '../custom-modal';
import Header from '../util/header';
import { CountrySelect } from './country-select';
import { MapCoordinates } from './maps-coordinates';
import { Language } from '../../languages/languageHandler';
import { ICurrency, ICurrencyValuesForm } from '../../../libraries/currencies';
import ChooseEntityModal from '../../helpdesk-app/components/choose-entity-modal';
import Input from '../../components/inputs';
import CurrencyTable from '../../pages/equipment-config/currencies-table';
import { useCurrencyEdit } from '../../utils/hooks';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import './styles.scss';

enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new',
  DEFAULT = 'default'
}

interface LocationFormProps {
  lang: Language;
  location: ILocation | null;
  currencyList: ICurrency[];
  selectedEntity?: number;
  loading: boolean;
  mode?: MODE;
  workflow?: 'helpdesk' | 'locals';
  reloadLocations?: () => void;
  clear?: () => void;
}

const LocationsForm: React.FC<LocationFormProps> = ({
  lang,
  location,
  currencyList,
  loading,
  mode = MODE.DEFAULT,
  workflow,
  reloadLocations,
  clear,
  selectedEntity
}) => {
  const navigate = useNavigate();
  const controller = new AbortController();
  const maxCurrencies = useSelector<AppState, number>(state => state.login.entity?.maxCurrencies || 1);

  const [selectedCurrencies, setSelectedCurrencies] = useState<ICurrencyValuesForm[]>([]);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [declarationAmount] = useState<number | undefined>(undefined);

  const [chooseEntityModal, setChooseEntityModal] = useState<boolean>(false);
  const filteredCid = currencyList.filter(c => !selectedCurrencies.find(sc => sc.cid === c.cid));

  const [isNewLocation, setNewLocation] = useState(true);
  const currencyEdit = useCurrencyEdit(selectedCurrencies, setSelectedCurrencies);

  useEffect(() => {
    if (selectedEntity) {
      form.reset();
      form.setFieldValue('entid', selectedEntity);

      setSelectedCurrencies([]);
      setCoordinatesInput('0, 0');
      setIsMapOpen(false);
      setNewLocation(true);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const initialFormValues = useMemo(
    () => ({
      sid: location?.sid ?? undefined,
      entid: location?.entid ?? selectedEntity,
      name: location?.name || '',
      description: location?.description || '',
      address: location?.address || '',
      postalCode: location?.postalCode || '',
      country: location?.country || '',
      lat: location?.lat ?? 0,
      lng: location?.lng ?? 0,
      external: location?.external || '',
      cbendpoint: location?.cbendpoint || '',
      currencies: location?.currencies || []
    }),
    [location, selectedEntity]
  );

  const form = useForm({ initialValues: initialFormValues });

  useEffect(() => {
    if (location) {
      form.setValues({
        sid: location.sid,
        entid: location.entid || selectedEntity,
        name: location.name || '',
        description: location.description || '',
        address: location.address || '',
        postalCode: location.postalCode || '',
        country: location.country || '',
        lat: location.lat || 0,
        lng: location.lng || 0,
        external: location.external || '',
        cbendpoint: location.cbendpoint || '',
        currencies: location.currencies || []
      });

      const mappedCurrencies: ICurrencyValuesForm[] = (location.currencies || []).map(c => ({
        gid: c.gid ?? 0,
        entid: c.entid ?? 0,
        sid: c.sid,
        cid: c.cid,
        minAmount: c.minAmount ?? 0,
        maxAmount: c.maxAmount ?? 0,
        decAmount: c.decAmount ?? 0
      }));

      setSelectedCurrencies(mappedCurrencies);
      setIsMapOpen(false);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedEntity]);

  const handleAddCurrency = (currency: any) => {
    if (!selectedCurrencies.find(c => c.cid === currency.cid)) {
      const updatedCurrencies = [...selectedCurrencies, currency];
      setSelectedCurrencies(updatedCurrencies);
    }
  };

  const handleRemoveCurrency = (cid: number) => {
    const updated = selectedCurrencies.filter(c => c.cid !== cid);
    setSelectedCurrencies(updated);
  };

  const submitForm = async (formData: any) => {
    const payload = {
      ...formData,
      currencies: selectedCurrencies,
      entid: formData.entid
    };
    const optionalKeys = [
      'sid',
      'description',
      'external',
      'cbendpoint',
      'address',
      'postalCode',
      'country',
      'lat',
      'lng'
    ];
    optionalKeys.forEach(key => {
      if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });

    const effectiveMode = mode === MODE.DEFAULT ? (formData.sid ? MODE.EDIT : MODE.NEW) : mode;

    try {
      if (effectiveMode === MODE.NEW) {
        const response = await addLocation(payload, controller);
        if (response.statusOper.code !== 0) {
          throw new Error(response.statusOper.message || 'Error adding location');
        }
        setSuccessMessage(lang.LOCATION_ADD_ALERT_SUCCESS || 'Location added successfully');
        setShowSuccessDialog(true);
        setTimeout(() => setShowSuccessDialog(false), 2000);

        const newLocation = response.shops?.[0];
        if (newLocation?.sid && newLocation?.entid) {
          if (workflow === 'helpdesk') {
            navigate(`/helpdesk/location/${MODE.EDIT}/${newLocation.entid}/${newLocation.sid}`);
          } else {
            navigate(`/locals`);
          }
        }
      } else if (effectiveMode === MODE.EDIT) {
        const response = await updateLocation(payload, controller);
        if (response.statusOper.code !== 0) {
          throw new Error(response.statusOper.message || 'Error updating location');
        }
        setSuccessMessage(lang.LOCATION_UPDATE_ALERT_SUCCESS || 'Location updated successfully');
        setShowSuccessDialog(true);
        setTimeout(() => setShowSuccessDialog(false), 2000);
      }
    } catch (error: any) {
      setErrorMessage(error.message || String(error));
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  const handleDeleteLocation = async (entid: number, sid: number) => {
    if (!sid) return;
    try {
      const response = await deleteLocation(entid, sid);
      if (response.statusOper.code === 0) {
        setSuccessMessage(lang.LOCATION_DELETE_ALERT_SUCCESS || 'Location deleted successfully');
        setShowSuccessDialog(true);
        form.reset();
        setSelectedCurrencies([]);
        setTimeout(() => {
          setShowSuccessDialog(false);
          if (mode === MODE.DEFAULT && reloadLocations) {
            reloadLocations();
          } else {
            navigate('/helpdesk/locations');
          }
        }, 2000);
      } else {
        throw new Error(response.statusOper.message || 'Delete failed');
      }
    } catch (error: any) {
      setErrorMessage(error.message || String(error));
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  const [coordinatesInput, setCoordinatesInput] = useState(`${form.values.lat}, ${form.values.lng}`);

  useEffect(() => {
    setCoordinatesInput(`${form.values.lat}, ${form.values.lng}`);
  }, [form.values.lat, form.values.lng]);

  const isReadMode = mode === MODE.READ;
  const isDefaultMode = mode === MODE.DEFAULT;

  const toggleChooseEntityModal = () => {
    setChooseEntityModal(!chooseEntityModal);
  };

  const chooseEntid = (entid: number) => {
    form.setFieldValue('entid', entid);
    setChooseEntityModal(false);
  };

  const clearEntid = () => {
    form.setFieldValue('entid', 0);
  };

  const handleEntidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      form.setFieldValue('entid', value === '' ? 0 : parseInt(value));
    }
  };

  const handleSidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      form.setFieldValue('sid', value === '' ? undefined : parseInt(value));
    }
  };

  const handleClearForm = () => {
    form.setValues(initialFormValues);
    form.setFieldValue('entid', selectedEntity);
    form.resetDirty();

    setSelectedCurrencies([]);
    setCoordinatesInput(`${initialFormValues.lat}, ${initialFormValues.lng}`);
    setIsMapOpen(false);
    setNewLocation(true);

    clear && clear();
  };

  return (
    <Group className="locals-edit">
      <ChooseEntityModal
        lang={lang}
        isOpen={chooseEntityModal}
        hideBtn={toggleChooseEntityModal}
        chooseEntid={chooseEntid}
      />

      {/* Entity Selector */}
      {mode === 'new' && (
        <Input
          label={lang.USER_ENTITY}
          value={form.values.entid !== undefined && form.values.entid !== 0 ? String(form.values.entid) : ''}
          disabled={!isNewLocation}
          isEditable
          search={toggleChooseEntityModal}
          onChange={handleEntidChange}
          onDelete={clearEntid}
        />
      )}

      {(mode === MODE.NEW && form.values.entid && form.values.entid !== 0) || mode !== MODE.NEW ? (
        <form onSubmit={form.onSubmit(submitForm)} className="locals-form">
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="ID"
                placeholder={lang.ENTITY_AUTO_ENTID}
                disabled={isReadMode}
                value={form.values.sid !== undefined ? String(form.values.sid) : ''}
                onChange={handleSidChange}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput
                label={lang.GLOBAL_NAME}
                required
                disabled={isReadMode}
                {...form.getInputProps('name')}
                value={form.values.name}
              />
            </Grid.Col>
          </Grid>
          <TextInput
            label={lang.EQUIPMENT_DESCRIPTION}
            disabled={isReadMode}
            {...form.getInputProps('description')}
            value={form.values.description}
          />
          <TextInput
            label={lang.GLOBAL_ADDRESS}
            disabled={isReadMode}
            {...form.getInputProps('address')}
            value={form.values.address}
          />
          <Grid>
            <Grid.Col span={6}>
              <CountrySelect
                form={form}
                label={lang.GLOBAL_COUNTRY}
                placeholder={lang.LOCATIONS_COUNTRY_SELECT}
                disabled={isReadMode}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label={lang.EVENT_ZIP_CODE}
                disabled={isReadMode}
                {...form.getInputProps('postalCode')}
                value={form.values.postalCode}
              />
            </Grid.Col>
          </Grid>
          <Grid grow gutter="lg" className="coordinates">
            <Grid.Col span={{ xs: 12, sm: 9, md: 8, lg: 8 }}>
              <TextInput
                label={`${lang.GLOBAL_LATITUDE} & ${lang.GLOBAL_LONGITUDE}`}
                placeholder="ex: 38.81197384388527, -9.21435085836787"
                value={coordinatesInput}
                disabled={isReadMode}
                onChange={e => setCoordinatesInput(e.target.value)}
                onBlur={() => {
                  const [latStr, lngStr] = coordinatesInput.split(',');
                  const lat = parseFloat(latStr?.trim());
                  const lng = parseFloat(lngStr?.trim());
                  if (!isNaN(lat)) form.setFieldValue('lat', lat);
                  if (!isNaN(lng)) form.setFieldValue('lng', lng);
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ xs: 12, sm: 3, md: 4, lg: 4 }}>
              <Button
                variant={!form.values.country ? 'outline' : 'filled'}
                onClick={() => setIsMapOpen(prev => !prev)}
                disabled={isReadMode || !form.values.country}
                className="map-button"
                type="button"
                radius="md"
              >
                {isMapOpen ? lang.LOCATIONS_CLOSE_MAP : lang.LOCATIONS_OPEN_MAP}
              </Button>
            </Grid.Col>

            {isMapOpen && (
              <Grid.Col span={12}>
                <MapCoordinates
                  address={form.values.address}
                  postalCode={form.values.postalCode}
                  country={form.values.country || ''}
                  lat={form.values.lat}
                  lng={form.values.lng}
                  onSelectCoordinates={(lat, lng) => {
                    form.setFieldValue('lat', lat);
                    form.setFieldValue('lng', lng);
                  }}
                />
              </Grid.Col>
            )}
          </Grid>
          <Accordion variant="unstyled" radius="md" className="accordion">
            <Accordion.Item value="additional-info">
              <Accordion.Control>{lang.GLOBAL_ADDITIONAL_INFORMATION}</Accordion.Control>
              <Accordion.Panel>
                <Group className="locals-currency-table">
                  <Header pageTitle={lang.GLOBAL_CURRENCY} />
                  <CurrencyTable
                    selectedCurrencies={selectedCurrencies}
                    currencyList={currencyList}
                    loading={loading}
                    editable={!isReadMode}
                    filteredCid={filteredCid}
                    onAddCurrency={handleAddCurrency}
                    onRemoveCurrency={handleRemoveCurrency}
                    form={form}
                    entity={{ entid: location?.entid || 0 }}
                    declarationAmount={declarationAmount}
                    isNewLocation={mode === MODE.NEW}
                    onCurrencyChange={(index, field, centsValue) => {
                      const updated = [...selectedCurrencies];
                      updated[index] = { ...updated[index], [field]: centsValue };
                      setSelectedCurrencies(updated);
                    }}
                    editingCell={null}
                    cellValue={''}
                    currencyEdit={currencyEdit}
                    maxCurrencies={maxCurrencies}
                  />
                </Group>

                <JsonInput
                  label={lang.ENTITY_EXTERNAL_DATA}
                  placeholder={`${lang.ENTITY_EXTERNAL_DATA} (JSON)`}
                  value={form.values.external}
                  onChange={value => form.setFieldValue('external', value)}
                  minRows={1}
                  disabled={isReadMode}
                />

                <TextInput
                  label={'C.B. Endpoint'}
                  placeholder={'Core Bank Endpoint'}
                  {...form.getInputProps('cbendpoint')}
                  value={form.values.cbendpoint}
                  disabled={isReadMode}
                />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {!isReadMode && (
            <Group>
              {(isDefaultMode || mode === MODE.NEW || mode === undefined) && (
                <Button color="#e4ae00" onClick={handleClearForm} type="button">
                  {lang.GLOBAL_NEW}
                </Button>
              )}

              <Button
                radius="md"
                type="submit"
                color="#538539"
                onClick={() => {
                  setNewLocation(true);
                  setTimeout(() => {
                    reloadLocations && reloadLocations();
                  }, 1500);
                }}
              >
                {lang.GLOBAL_SAVE_BUTTON}
              </Button>

              {(isDefaultMode || mode === MODE.EDIT) && (
                <Button
                  radius="md"
                  color="#861724"
                  onClick={() => {
                    handleDeleteLocation(location?.entid || 0, form.values.sid || 0);
                  }}
                  type="button"
                >
                  {lang.GLOBAL_DELETE}
                </Button>
              )}
            </Group>
          )}
        </form>
      ) : (
        <Text fs="italic">{lang.LOCATION_CHOOSE_ENTID}</Text>
      )}

      <CustomModal
        opened={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        backgroundColor="#3BDF70"
        textColor="black"
      />

      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E33535"
        textColor="white"
      />
    </Group>
  );
};

export default LocationsForm;
